import { useRef, useCallback } from 'react';
import { fetchUnifiedAI } from '../../../utils/apiHooks';
import { AVAILABLE_DATA_SOURCES } from '../../../utils/mockDataRegistry';
import { generateWidgetSummaries } from '../../../utils/widgetSummary';

/**
 * Send message flow: API call, Phase 1/2 handling, PII handling.
 * Uses a ref for handleSendMessage so it can call itself recursively for Phase 2.
 */
export function useSendMessage(deps) {
    const {
        messagesRef,
        setMessages,
        currentPhase,
        setCurrentPhase,
        setPendingWidgetId,
        pendingWidgetIdRef,
        getRecentHistory,
        getFullHistory,
        setError,
        setPendingPiiResolution,
        setPiiSearchQueries,
        setInputValue,
        inputRef,
        setIsTyping,
        state,
        exchangeIdRef,
    } = deps;

    const handleSendMessageRef = useRef(null);

    const handleSendMessage = useCallback(
        async ({ overrideText, phaseOverride, skipUserMessage, intentData } = {}) => {
            const messageText = overrideText || (deps.inputValueRef?.current ?? '').trim();
            if (!messageText) return;

            if (!skipUserMessage) {
                const userMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'user',
                    content: messageText,
                    timestamp: new Date(),
                };
                setMessages((prev) => [...prev, userMessage]);
                setInputValue('');
                if (inputRef?.current) {
                    inputRef.current.textContent = '';
                }
            }

            setError(null);
            if (deps.setPendingWidgetRef?.current) deps.setPendingWidgetRef.current(null);
            setPendingPiiResolution?.(null);
            setPiiSearchQueries?.({});

            const phase = phaseOverride || currentPhase;
            setIsTyping(true);

            try {
                const availableDataSources = AVAILABLE_DATA_SOURCES.map((source) => ({
                    id: source.id,
                    label: source.label,
                    description: source.description,
                    metricCategory: source.metricCategory,
                    dataType: source.dataType,
                    availableContexts: source.availableContexts,
                    availablePeriods: source.availablePeriods,
                    defaultColor: source.defaultColor,
                }));

                let requestBody;

                if (phase === 'intent') {
                    const summaries = generateWidgetSummaries(state.report.widgets, state.report.layout.lg || []);
                    const currentMessages = messagesRef?.current ?? [];
                    requestBody = {
                        message: messageText,
                        conversationHistory: getRecentHistory(currentMessages, 5),
                        availableDataSources,
                        availableWidgets: summaries,
                        phase: 'intent',
                        conversationId: deps.conversationIdRef?.current,
                        exchangeId: exchangeIdRef.current,
                    };
                } else {
                    const summaries = generateWidgetSummaries(state.report.widgets, state.report.layout.lg || []);
                    const currentMessages = messagesRef?.current ?? [];
                    requestBody = {
                        message: messageText,
                        conversationHistory: getFullHistory(currentMessages),
                        availableDataSources,
                        availableWidgets: summaries,
                        phase: 'execute',
                        mcpToolInfo: intentData
                            ? {
                                  action: intentData.action,
                                  server: intentData.server,
                                  tool: intentData.tool,
                                  arguments: intentData.arguments,
                              }
                            : undefined,
                        conversationId: deps.conversationIdRef?.current,
                        exchangeId: exchangeIdRef.current,
                    };

                    const widgetIdToUse = deps.pendingWidgetId ?? pendingWidgetIdRef?.current;
                    if (widgetIdToUse) {
                        const widget = state.report.widgets[widgetIdToUse] ?? null;
                        const layout = state.report.layout.lg?.find((item) => item.i === widgetIdToUse) ?? null;
                        if (widget) {
                            requestBody.currentWidget = widget;
                            if (layout) requestBody.currentLayout = layout;
                        }
                    }
                }

                const response = await fetchUnifiedAI(requestBody);
                const data = response?.result;
                const conversationId = response?.conversation_id;
                const exchangeId = response?.exchange_id;

                if (conversationId && deps.conversationIdRef && !deps.conversationIdRef.current) {
                    deps.conversationIdRef.current = conversationId;
                }
                if (exchangeId && exchangeId != exchangeIdRef.current) exchangeIdRef.current = exchangeId;

                const currentAction = data?.action; //
                const sanitiseObj = data?.sanitise_obj;
                const fallbackToolChosen = data?.arguments?.fallback_reason; // attribute that outlines why 'fallback_tool' has been chosen
                const feedbackIds =
                    conversationId || exchangeId
                        ? { conversationId: conversationId || null, exchangeId: exchangeId || null }
                        : undefined;

                if (phase === 'intent') {
                    setCurrentPhase('execute');

                    if (data.intent === 'edit' && data.matchingWidgets?.length === 1) {
                        setPendingWidgetId(data.matchingWidgets[0].id);
                        if (pendingWidgetIdRef) pendingWidgetIdRef.current = data.matchingWidgets[0].id;
                    }

                    const assistantMessage = {
                        id: `msg-${Date.now() + 1}`,
                        role: 'assistant',
                        content: data.message,
                        timestamp: new Date(),
                        dismissed: false,
                        suggestedPrompts: data.suggestedPrompts,
                        feedbackIds,
                    };

                    setMessages((prev) => [...prev, assistantMessage]);

                    if (sanitiseObj?.has_pii === true) {
                        setPendingPiiResolution?.({
                            sanitizedMessage: sanitiseObj.sanitized_message,
                            entities: sanitiseObj.entities || {},
                        });
                        setInputValue(sanitiseObj.sanitized_message);
                        setCurrentPhase('intent');
                        setIsTyping(false);
                        return;
                    }

                    if (fallbackToolChosen || currentAction == 'direct_response') {
                        setCurrentPhase('intent');
                        return;
                    }

                    await handleSendMessageRef.current?.({
                        overrideText: messageText,
                        phaseOverride: 'execute',
                        skipUserMessage: true,
                        intentData: data,
                    });
                    setCurrentPhase('intent');
                    return;
                }

                const assistantMessage = {
                    id: `msg-${Date.now() + 1}`,
                    role: 'assistant',
                    content: data.message,
                    timestamp: new Date(),
                    edit:
                        data.intent === 'edit'
                            ? {
                                  widget: data.widget,
                                  layout: data.layout,
                                  message: data.message,
                                  changes: data.changes,
                              }
                            : undefined,
                    dismissed: false,
                    suggestedPrompts: data.suggestedPrompts,
                    feedbackIds,
                };

                setMessages((prev) => [...prev, assistantMessage]);
                setCurrentPhase('intent');
                setPendingWidgetId(null);
                if (pendingWidgetIdRef) pendingWidgetIdRef.current = null;
            } catch (err) {
                console.error('AI API error:', err);
                // setError(err?.message || 'Failed to communicate with Fae. Please try again.');
                const errorMessage = {
                    id: `msg-${Date.now() + 1}`,
                    role: 'assistant',
                    content: "I'm sorry, I encountered an error while processing your request. Please try again.",
                    timestamp: new Date(),
                };
                setMessages((prev) => [...prev, errorMessage]);
                setCurrentPhase('intent');
            } finally {
                setIsTyping(false);
            }
        },
        [
            currentPhase,
            setMessages,
            setCurrentPhase,
            setPendingWidgetId,
            getRecentHistory,
            getFullHistory,
            setError,
            setPendingPiiResolution,
            setPiiSearchQueries,
            setInputValue,
            setIsTyping,
            state,
            deps.conversationIdRef,
            deps.pendingWidgetId,
            pendingWidgetIdRef,
            inputRef,
            exchangeIdRef,
        ],
    );

    handleSendMessageRef.current = handleSendMessage;

    return { handleSendMessage };
}
